import React, { ReactNode } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

import { SortDirection } from '../../services/serviceJobsAccount/interfaces';

export interface IAccountTableColumn<T> {
  id: string;
  label: ReactNode;
  render: (item: T) => ReactNode;
  align?: 'left' | 'center' | 'right';
  sortBy?: string;
  width?: string | number;
}

interface IAccountTableProps<T> {
  ariaLabel: string;
  columns: IAccountTableColumn<T>[];
  rows: T[];
  getRowId: (item: T) => string | number;
  emptyMessage: string;
  isLoading?: boolean;
  sortBy?: string;
  sortDirection?: SortDirection;
  onSort?: (sortBy: string, direction: SortDirection) => void;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
}

export const AccountTable = <T extends unknown>({
  ariaLabel,
  columns,
  rows,
  getRowId,
  emptyMessage,
  isLoading,
  sortBy,
  sortDirection = 'asc',
  onSort,
  page,
  pageSize,
  total,
  onPageChange,
}: IAccountTableProps<T>) => {
  const hasPagination = page !== undefined
    && pageSize !== undefined
    && total !== undefined
    && Boolean(onPageChange);

  const requestSort = (column: IAccountTableColumn<T>) => {
    if (!column.sortBy || !onSort) return;
    const direction: SortDirection = sortBy === column.sortBy && sortDirection === 'asc'
      ? 'desc'
      : 'asc';
    onSort(column.sortBy, direction);
  };

  return (
    <Paper variant="outlined">
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table aria-label={ariaLabel}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  sortDirection={sortBy === column.sortBy ? sortDirection : false}
                  sx={{ width: column.width }}
                >
                  {column.sortBy && onSort ? (
                    <TableSortLabel
                      active={sortBy === column.sortBy}
                      direction={sortBy === column.sortBy ? sortDirection : 'asc'}
                      onClick={() => requestSort(column)}
                      sx={{
                        color: 'inherit !important',
                        '&:hover': { color: 'inherit' },
                        '&.Mui-active': { color: 'inherit' },
                        '& .MuiTableSortLabel-icon': {
                          color: 'inherit !important',
                        },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <CircularProgress size={28} aria-label="Loading" />
                  </Box>
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading && rows.map((row) => (
              <TableRow key={getRowId(row)} hover>
                {columns.map((column) => (
                  <TableCell key={column.id} align={column.align || 'left'}>
                    {column.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {hasPagination ? (
        <TablePagination
          component="div"
          count={total as number}
          page={(page as number) - 1}
          rowsPerPage={pageSize as number}
          rowsPerPageOptions={[pageSize as number]}
          onPageChange={(_event, nextPage) => (onPageChange as (value: number) => void)(nextPage + 1)}
        />
      ) : null}
    </Paper>
  );
};

export default AccountTable;
